import TurndownService from 'turndown';

const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-', // match MDXEditor's own bullet style
});

// Special marker for empty paragraphs (zero-width space + newline)
const EMPTY_PARAGRAPH_MARKER = '\u200B';

/**
 * Detect if content is HTML (vs markdown).
 */
export function isHtmlContent(content: string): boolean {
  if (!content) return false;
  // Check for common HTML patterns
  const htmlPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlPattern.test(content);
}

/**
 * Convert HTML to Markdown.
 */
export function htmlToMarkdown(html: string): string {
  return turndown.turndown(html);
}

// AB#555: MDXEditor (no raw-HTML plugin) escapes raw block tags typed into its
// Source tab into literal &lt;h2&gt;-style entities inside the markdown field;
// the SSG then renders that HTML source to visitors. Trigger only on an escaped
// entity immediately followed by a known BLOCK tag name — never on prose that
// merely mentions `&lt;` (e.g. "x &lt; y").
const ESCAPED_BLOCK_TAG =
  /&lt;\/?(h[1-6]|ul|ol|li|strong|em|p|br|a|blockquote|table)\b/i;

/**
 * Heal a body that has HTML block tags escaped into text (AB#555).
 * Unescapes the entities back to real markup, then runs the existing
 * htmlToMarkdown so what we keep is clean markdown. No-op when not triggered.
 */
export function repairEscapedHtml(content: string): string {
  if (!ESCAPED_BLOCK_TAG.test(content)) return content;
  // Unescape (&amp; last so single-escaped `&amp;lt;` doesn't double-decode).
  const html = content
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
  return htmlToMarkdown(html);
}

/**
 * Normalize content for editor.
 * Converts HTML to markdown if needed, so MDXEditor can handle it.
 * Restores empty paragraphs from markers.
 */
export function normalizeForEditor(content: string): string {
  if (!content) return '';
  
  let result = content;

  if (ESCAPED_BLOCK_TAG.test(result)) {
    // Corrupted body: heal it so it loads as clean markdown instead of
    // degrading further on each open+save (AB#555).
    result = repairEscapedHtml(result);
  } else if (isHtmlContent(result)) {
    result = htmlToMarkdown(result);
  }
  
  // Restore empty paragraph markers to actual empty lines for the editor
  // This helps preserve visual spacing when content is reloaded
  result = result.replace(new RegExp(EMPTY_PARAGRAPH_MARKER, 'g'), '');
  result = preserveEmptyParagraphs(result);

  return result;
}

/**
 * Normalize content for saving.
 * Preserves empty paragraphs by detecting triple+ newlines.
 */
export function normalizeForSave(content: string): string {
  if (!content) return '';
  // Backstop: never persist escaped block-level HTML (AB#555).
  return preserveEmptyParagraphs(repairEscapedHtml(content));
}

/**
 * Replace runs of 3+ newlines with non-breaking space paragraph spacers.
 * In markdown, \u00a0 is not treated as whitespace, so these paragraphs
 * survive parsing round-trips while rendering as empty-looking lines.
 */
function preserveEmptyParagraphs(markdown: string): string {
  return markdown.replace(/\n{3,}/g, (match) => {
    const extraNewlines = match.length - 2;
    const emptyParagraphs = Math.ceil(extraNewlines / 2);
    return '\n\n' + '\u00a0\n\n'.repeat(emptyParagraphs);
  });
}
